import { useState, type FormEvent } from "react";

const FORM_URL = "https://readdy.ai/api/form/daete3jep053ijub1h50";

const TOPICS = ["人设反转线索", "身份揭秘线索", "滤镜打假线索", "言行双标线索", "单纯投稿/合作"];

type Status = "idle" | "submitting" | "success" | "error";

export default function ContributeSection() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);

    // 反垃圾蜜罐：若被机器人填充则不向表单地址提交任何内容，仅展示正常反馈
    const honeypot = ((fd.get("website_alt") as string) || "").trim();
    if (honeypot) {
      setStatus("success");
      form.reset();
      return;
    }

    const payload = new URLSearchParams();
    fd.forEach((value, key) => {
      if (key === "website_alt") return;
      if (typeof value === "string" && value.trim()) payload.append(key, value);
    });

    setStatus("submitting");
    setFormError("");
    try {
      const resp = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const text = await resp.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(text);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || "";
      const isOk = resp.ok && parsed?.code === "OK" && !/spam/i.test(text);
      if (isOk) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        const msg = serverMsg && !/spam/i.test(serverMsg) ? serverMsg : "提交失败，请稍后重试，或换个更清晰的事件描述。";
        setFormError(msg);
      }
    } catch {
      setStatus("error");
      setFormError("网络似乎开小差了，请稍后再试一次。");
    }
  };

  return (
    <section id="contribute" className="scroll-mt-20 py-12 md:py-16">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="overflow-hidden rounded-3xl bg-foreground-900">
          <div className="grid grid-cols-1 gap-0 lg:grid-cols-2">
            {/* 左侧说明 */}
            <div className="relative flex flex-col justify-center overflow-hidden p-8 md:p-12">
              <span className="pointer-events-none absolute -right-10 -top-10 h-40 w-40 rounded-full bg-accent-400/20 blur-2xl" />
              <p className="inline-flex w-max items-center gap-2 rounded-full bg-accent-400/15 px-3 py-1 text-xs font-semibold text-accent-300">
                <i className="ri-flashlight-fill" /> TIP US OFF
              </p>
              <h2 className="mt-4 font-heading text-3xl font-black leading-tight text-background-50">
                有反转线索？
                <br />
                把「光鲜背后」交给我们深扒
              </h2>
              <p className="mt-4 max-w-md text-sm leading-7 text-background-200/80">
                如果你掌握某个人设与事实不符的可核实线索（公开报道、聊天记录、第三方资料等），欢迎匿名投递。编辑团队会先做交叉验证，再决定是否跟进，你的隐私会得到保护。
              </p>
              <ul className="mt-6 space-y-2.5 text-sm text-background-200/70">
                {[
                  "仅接受公开、可核实的线索，拒绝造谣与网暴",
                  "投稿可匿名，你的身份信息不会被公开",
                  "采用后会在文章中标注「线索来源」，引用时隐去隐私",
                ].map((t) => (
                  <li key={t} className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-fill mt-0.5 text-accent-400" />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* 右侧表单 */}
            <div className="bg-background-50 p-8 md:p-12">
              <h3 className="font-heading text-xl font-bold text-foreground-950">提交你的线索</h3>
              <p className="mt-1 text-sm text-foreground-500">带 * 号为必填项，正文限 500 字以内。</p>

              <form
                id="fancha-contribute-form"
                data-readdy-form="fancha-contribute"
                onSubmit={handleSubmit}
                className="mt-6 space-y-4"
              >
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label htmlFor="fc-name" className="mb-1.5 block text-sm font-medium text-foreground-800">
                      你的称呼
                    </label>
                    <input
                      id="fc-name"
                      name="name"
                      type="text"
                      required
                      maxLength={30}
                      placeholder="怎么称呼你（可匿名）"
                      className="w-full rounded-md border border-background-200 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                    />
                  </div>
                  <div>
                    <label htmlFor="fc-email" className="mb-1.5 block text-sm font-medium text-foreground-800">
                      联系邮箱
                    </label>
                    <input
                      id="fc-email"
                      name="email"
                      type="email"
                      required
                      maxLength={80}
                      placeholder="用于接收跟进反馈"
                      className="w-full rounded-md border border-background-200 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="fc-topic" className="mb-1.5 block text-sm font-medium text-foreground-800">
                    线索类型
                  </label>
                  <select
                    id="fc-topic"
                    name="topic"
                    className="w-full cursor-pointer rounded-md border border-background-200 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                  >
                    {TOPICS.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="fc-message" className="mb-1.5 block text-sm font-medium text-foreground-800">
                    线索详情 <span className="font-normal text-foreground-400">（请附可核实的信息来源）</span>
                  </label>
                  <textarea
                    id="fc-message"
                    name="message"
                    required
                    maxLength={500}
                    rows={5}
                    placeholder="请描述事件经过、你的证据以及期望我们核实的点……"
                    className="w-full resize-none rounded-md border border-background-200 bg-background-50 px-3.5 py-2.5 text-sm leading-6 text-foreground-900 outline-none placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                  />
                  <p className="mt-1 text-right text-xs text-foreground-400">最多 500 字</p>
                </div>

                {/* 反垃圾蜜罐字段 */}
                <input
                  type="text"
                  name="website_alt"
                  aria-hidden="true"
                  autoComplete="off"
                  tabIndex={-1}
                  readOnly
                  className="fc-field-verify"
                />

                {status === "error" && formError && (
                  <p className="rounded-md bg-primary-50 px-3.5 py-2.5 text-sm text-primary-700">
                    <i className="ri-error-warning-line mr-1.5" />
                    {formError}
                  </p>
                )}
                {status === "success" && (
                  <p className="rounded-md bg-accent-100 px-3.5 py-2.5 text-sm text-accent-800">
                    <i className="ri-checkbox-circle-line mr-1.5" />
                    已收到你的线索，感谢支持！编辑团队会尽快交叉验证并跟进。
                  </p>
                )}

                <button
                  type="submit"
                  disabled={status === "submitting"}
                  className="inline-flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-70"
                >
                  {status === "submitting" ? (
                    <>
                      <i className="ri-loader-4-line animate-spin" /> 正在提交…
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-fill" /> 提交线索
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}